import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// @mui

import { Badge, Box, Container, List, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

import NavConfig from "./config";
import useResponsive from "../../../hooks/useResponsive";
import SearchInput from "../../../components/ui-elements/search-input";
import { PostButton } from "./post-button";
import {
  PATH_ACCOUNT,
  PATH_DASHBOARD,
  PATH_ONBOARD,
} from "../../../routes/path";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import UnverifiedModalContent from "../../../components/unverified-modal";
import { motion, useAnimation } from "framer-motion";
import { Howl } from "howler";
import useEcho from "../../../hooks/echo";
import { NotificationContent } from "./notification-content";
import { isCurrentPath } from "../../../utils/random-functions";
import axios from "axios";
import Axios from "../../../utils/http-request";
import {
  receiveWebSocketMessage,
  setUnreadCount,
  updateChatMessages,
} from "../../../store/messageSlice";

import { useNotificationService } from "../../../hooks/notification";
import { apiPostReadMessage } from "../../../api/messages";

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

interface NavItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  colorIcon: React.ReactNode;
}

interface NavSectionProps {
  data: () => NavItem[]; // Define the type of the data prop
}

const NavStyle = styled("div")(({ theme }) => ({
  height: "100vh",
  flexShrink: 0,
  [theme.breakpoints.up("lg")]: {
    paddingTop: 34,
  },
}));
const Navigation = () => {
  const isDesktop = useResponsive("down", "lg", "xl");
  const isMedium = useResponsive("down", "md", "xl");
  const navigate = useNavigate();
  const controls = useAnimation();
  const dispatch = useDispatch();

  const echo = useEcho();
  const id = useSelector(
    (state: RootState) => state.content.account_profile_data
  ).id;
  const chatMainId = useSelector((state: RootState) => state.message.chartId);
  const notification_data = useSelector(
    (state: RootState) => state.content.notification_data
  );
  const receiver_id = useSelector(
    (state: RootState) => state.message.receiverId
  );
  const sound = new Howl({
    src: ["/media/bell.mp3"],
  });

  // Trigger chat message count
  const triggerAnimation = () => {
    controls.start({
      scale: [1, 1.5, 1],
      transition: {
        duration: 0.5,
        ease: "easeInOut",
        repeat: 1,
        repeatType: "mirror",
      },
    });
  };

  const handleEchoCallback = () => {
    triggerAnimation();
    sound.play();
  };

  const channelRef = useRef<any>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout>();
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const mountedRef = useRef(true);

  const fetchUnreadMessages = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const response = await Axios.get("/chats/unread", {
        params: { user_id: id },
      });

      if (mountedRef.current && Array.isArray(response.data)) {
        dispatch(setUnreadCount(response.data.length));
        dispatch(updateChatMessages(response.data));
      }
    } catch (error) {
      console.error("Error fetching unread messages:", error);
    }
  }, [id, dispatch]);

  const setupChannel = useCallback(() => {
    if (!echo || !id || !mountedRef.current) return;

    try {
      // Setup chat channel
      const chatId = [id, receiver_id].sort().join("_");
      const channel = echo.private(`chat.${chatId}`);

      channel.listen("NewMessage", (data: any) => {
        // Refresh unread messages when new message arrives
        dispatch(receiveWebSocketMessage(data));
        // Mark messages as read only if the chat is opened
        if (data?.receiver_id === chatMainId) {
          apiPostReadMessage(chatMainId);
        }
      });

      // Handle connection events
      channel.subscribed(() => {

        // Start ping to keep connection alive
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }

        pingIntervalRef.current = setInterval(() => {
          if (echo?.connector?.socket) {
            echo.connector.socket.emit("ping");
          }
        }, 30000);
      });

      channel.error((error: any) => {
        console.error("Channel error:", error);
        handleReconnect();
      });

      channelRef.current = channel;

      // Fetch initial unread messages
      fetchUnreadMessages();
    } catch (error) {
      console.error("Error setting up Echo channel:", error);
      handleReconnect();
    }
  }, [echo, id, receiver_id, fetchUnreadMessages]);

  const handleReconnect = useCallback(() => {
    if (!mountedRef.current) return;

    // Clear existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Attempt to reconnect after 3 seconds
    reconnectTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        setupChannel();
      }
    }, 3000);
  }, [setupChannel]);

  useEffect(() => {
    mountedRef.current = true;

    // Small delay to ensure Echo is ready
    const initTimeout = setTimeout(() => {
      if (mountedRef.current) {
        setupChannel();
      }
    }, 100);

    // Cleanup function
    return () => {
      mountedRef.current = false;

      // Clear all timeouts and intervals
      clearTimeout(initTimeout);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      // Leave channels
      if (echo && id) {
        const chatId = [id, receiver_id].sort().join("_");
        echo.leave(`chat.${chatId}`);
      }

      // Cleanup channel reference
      if (channelRef.current) {
        channelRef.current = null;
      }
    };
  }, [echo, id, receiver_id, setupChannel]);

  return (
    <NavStyle
      sx={{
        position: "fixed",
        width: "100%",
        height: 80,
        borderBottom: "1px solid #E7EBEF",
        pt: "0 !important",
        background: "#FAFAFA",
      }}
    >
      <Container
        sx={{
          display: "flex",
          justifyContent: !isMedium ? "center" : "space-between",
          height: 80,
          paddingLeft: "0",
        }}
      >
        <Box
          sx={{
            borderRight: !isMedium ? "1px solid #E7EBEF" : "",
            height: 80,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            pr: isMedium ? 1 : 4,
            pl: isMedium ? 2 : 0,
          }}
          onClick={() => navigate(PATH_DASHBOARD.root)}
        >
          <img
            src="/assets/icons/icon-main.png"
            width="120"
            height="120"
            alt=""
            style={{ cursor: "pointer" }}
          />
        </Box>
        {!isMedium && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isDesktop ? "center" : "space-between",
              borderRight: !isMedium ? "1px solid #E7EBEF" : "",
              width: "70%",
              px: 3,
            }}
          >
            <SearchInput />
            <NavSection data={NavConfig} />
          </Box>
        )}
        <Box>
          <PostButton />
        </Box>
      </Container>
    </NavStyle>
  );
};

export default Navigation;

function NavSection({ data }: NavSectionProps) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useResponsive("down", "lg", "xl");
  const verified = useSelector((state: RootState) => state.content.verified);
  const account_profile = useSelector(
    (state: RootState) => state.content.account_profile_data
  );
  const [open, setOpen] = useState(false);
  const { showNotification } = useNotificationService();
  const handle_modal = () => setOpen(!open);

  const handlePathNavigate = (path: string) => {
    
    if (path === "/newsfeed") {
      navigate("/newsfeed", { replace: true });
      return;
    }
    if (!verified) {
      // check the verification process
      handleVerify(path);

      return;
    } else if (verified) {
      navigate(path);
    }
  };
  const handleVerify = (path: string) => {
    if (!account_profile.name && account_profile.kyced === 0) {
      // user has not filled the onboarding form
      handle_modal();
    } else if (account_profile.name && account_profile.id_uploaded === 0) {
      // user has not completed the onboarding form
      if (path === "/dashboard") {
        navigate(path);
        return;
      }
      handle_modal();
    } else if (account_profile.name && account_profile.id_uploaded === 1) {
      // user has completed the onboarding form and need to be verified
      if (path === "/dashboard") {
        navigate(path);
        return;
      }
      showNotification(
        "Account profile has been submitted, Verification in progress",
        "info"
      );
    }
  };
  const newData = data();
  return (
    <>
      <UnverifiedModalContent open={open} handle_modal={handle_modal} />
      <Box sx={{ mx: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <List
          disablePadding
          sx={{
            p: 1,
            my: 1,
            display: "flex",
            gap: isDesktop ? 5 : 3,
            justifyContent: "center",
          }}
        >
          {newData.map(
            (
              item: NavItem // Adjust mapping according to NavItem type
            ) => (
              <Box
                key={item.title}
                onClick={() => handlePathNavigate(item?.path)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                {isCurrentPath(item.path, location)
                  ? item.colorIcon
                  : item.icon}
                <Typography
                  sx={{
                    fontWeight: isCurrentPath(item.path, location) ? 500 : 400,
                    color: isCurrentPath(item.path, location)
                      ? theme.palette.primary.main
                      : theme.palette.secondary.contrastText,
                    fontSize: 12,
                    letterSpacing: "-0.11px",
                  }}
                >
                  {item.title}
                </Typography>{" "}
                {/* Remove extra curly braces */}
              </Box>
            )
          )}
        </List>
        <CheckAuth verified={verified} />
      </Box>
    </>
  );
}

function CheckAuth({ verified }: { verified: boolean }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const account_profile_data = useSelector(
    (state: RootState) => state.content.account_profile_data
  );
  const { showNotification } = useNotificationService();

  const handleVerifyCheck = () => {
    if (!account_profile_data.name && account_profile_data.id_uploaded === 0) {
      // user has not filled the onboarding form
      navigate(PATH_ONBOARD.root);
    } else if (
      account_profile_data.name &&
      account_profile_data.id_uploaded === 0
    ) {
      // user has not completed the onboarding form
      navigate(PATH_ONBOARD.account);
    } else if (
      account_profile_data.name &&
      account_profile_data.id_uploaded === 1
    ) {
      // user has completed the onboarding form and need to be verified
      showNotification(
        "Account profile has been submitted, Verification in progress",
        "info"
      );
    }
  };

  const isCurrentPath = (path: string) => {
    if (path === "") return false;
    return location.pathname.startsWith(path);
  };
  return (
    <>
      <NotificationContent />
      {account_profile_data.name ? (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => navigate(PATH_ACCOUNT.root)}
          >
            <img
              src={account_profile_data?.image}
              alt="v"
              width={26}
              height={26}
              style={{ borderRadius: "40px" }}
            />
            <Typography
              sx={{
                fontWeight: isCurrentPath("/account") ? 500 : 400,
                color: isCurrentPath("/account")
                  ? theme.palette.primary.main
                  : theme.palette.secondary.contrastText,
                fontSize: 12,
                letterSpacing: "-0.11px",
              }}
            >
              Account
            </Typography>{" "}
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={handleVerifyCheck}
        >
          <img
            src="/assets/icons/home/crown-icon.png"
            alt="i"
            width={20}
            height={20}
          />
          <Typography
            variant="caption"
            color="primary"
            sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
          >
            Personalise <br /> Account
          </Typography>
        </Box>
      )}
    </>
  );
}
